import React, { useState } from "react";
import "./MarkSubmissionTable.css";
import { useLocation, useParams } from "react-router-dom";
import PropTypes from "prop-types";
import { useConsortiaCenter } from "../../context/ConsortiaCenterContext";
import { getCenterType, checkunitIsConsortiaUnit } from "../../utils/utils";
import { useCenter } from "../../context/CenterContext";
import { useUnits } from "../../context/UnitsContext";
import { useSubject } from "../../context/SubjectContext";

const MarkSubmissionTable = ({
  availableAssignments,
  showError,
  rows,
  setRows,
  candidates,
  anyRowInvalid,
  isRowComplete,
}) => {
  const [sortKey, setSortKey] = useState("candidateName");
  const [sortDirection, setSortDirection] = useState("asc");
  const location = useLocation();
  const { unit } = useParams();
  const { centerNumber } = useCenter();
  const { consortiaCentres = [] } = useConsortiaCenter();
  const { consortiaUnits } = useUnits();
   const { sessionId } = location.state || {};
   const { subjectData } = useSubject();
   const { selectedSubject, selectedSubjectId } = subjectData || {}; 

  const currentCentreType =
    getCenterType(consortiaCentres, Number(centerNumber), selectedSubject, selectedSubjectId) || "";
 

    const cosortiaunitcode =
    String(unit ?? "")
      .toUpperCase()
      .match(/^[A-Z0-9]+/)?.[0] || "";
   const isConsortiaUnit = checkunitIsConsortiaUnit(
    consortiaCentres,
    centerNumber,
    sessionId,
    cosortiaunitcode,
  );

  // Helper to get sorted index mapping
  const sortedIndexes = [...candidates.keys()].sort((a, b) => {
    let compareVal = 0;
    if (sortKey === "status") {
      // Incomplete (missing marks or assignment) should come first
      const aComplete = isRowComplete(rows[a]);
      const bComplete = isRowComplete(rows[b]);
      compareVal =
        aComplete === bComplete
          ? 0
          : aComplete
            ? 1 // complete (green tick) after
            : -1; // incomplete (red exclamation) before
    } else if (sortKey === "candidateName") {
      compareVal = candidates[a].candidateName.localeCompare(
        candidates[b].candidateName,
      );
    } else if (sortKey === "candidateNumber") {
      compareVal = candidates[a].candidateNumber.localeCompare(
        candidates[b].candidateNumber,
      );
    }
    return sortDirection === "asc" ? compareVal : -compareVal;
  });

  // Handler for clicking column headers
  const onSortColumn = (key) => {
    if (sortKey === key) {
      setSortDirection((dir) => (dir === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
  };

  const handleInputChange = (idx, field, value) => {
    const newRows = [...rows];
    if (field === "absent") {
      newRows[idx].markAsAbsent = value;
      if (value) {
        newRows[idx].totalCriteriaAchieved = "";
        newRows[idx].assignmentId = "";
        newRows[idx].assignment = "";
      }
    } else if (field === "marks") {
      const regex = /^\d{0,2}$/;
      if (value === "" || regex.test(value)) {
        newRows[idx].totalCriteriaAchieved = value;
      }
    } else if (field === "assignmentId") {
      // Store both assignmentId and assignment label for download
      newRows[idx].assignmentId = value;
      const selectedAssignment = availableAssignments.find(
        (a) => String(a.assignmentId) === String(value)
      );
      newRows[idx].assignment = selectedAssignment?.assignmentLabel || "";
    } else {
      newRows[idx][field] = value;
    }
    setRows(newRows);
  };
  return (
    <div>
      {showError && anyRowInvalid && (
        <div
          className="notification is-danger is-flex mt-4"
          id="pnlErrors"
          style={{
            alignItems: "flex-start",
            textAlign: "left",
          }}
        >
          <span className="icon mr-4">
            <svg
              focusable="false"
              aria-hidden="true"
              viewBox="0 0 24 24"
              style={{ width: 24, height: 24 }}
            >
              <path d="M12 7c.55 0 1 .45 1 1v4c0 .55-.45 1-1 1s-1-.45-1-1V8c0-.55.45-1 1-1zm-.01-5C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm1-3h-2v-2h2v2z" />
            </svg>
          </span>
          <span>
            <strong>There are missing marks or assignments.</strong>
            <br />
            Please scroll down and make sure you have entered a valid mark and
            have selected an assignment for every candidate.
          </span>
        </div>
      )}
      <div className="table-container hide-icons">
        <table className="table is-hoverable mark-table">
          <colgroup>
            <col className="col-status" />
            <col className="col-number" />
            <col className="col-name" />
            <col className="col-absent" />
            <col className="col-marks" />
            <col className="col-assignment" />
          </colgroup>
          <thead>
            <tr className="table-row-fullwidth">
              <th
                onClick={() => onSortColumn("status")}
                style={{ cursor: "pointer" }}
              >
                Status
                <span className="status-caret">
                  {sortKey === "status"
                    ? sortDirection === "asc"
                      ? " ⇅"
                      : " ⇅"
                    : " ⇅"}
                </span>
              </th>
              <th
                style={{
                  display:
                    currentCentreType === "Lead" && isConsortiaUnit
                      ? "table-cell"
                      : "none", // Show only if both conditions are met
                }}
              >
                {currentCentreType === "Lead" && isConsortiaUnit ? "Centre number" : ""}
              </th>
              <th>
                Candidate <br /> number
              </th>
              <th
                onClick={() => onSortColumn("candidateName")}
                style={{ cursor: "pointer" }}
              >
                Candidate name
                <span>
                  {sortKey === "candidateName"
                    ? sortDirection === "asc"
                      ? " ⇅"
                      : " ⇅"
                    : " ⇅"}
                </span>
              </th>
              <th>Absent?</th>
              <th>
                Total number of
                <br />
                criteria achieved
              </th>
              <th>Assignment</th>
            </tr>
          </thead>
          <tbody>
            {sortedIndexes.map((idx) => {
              const candidate = candidates[idx];
              const row = rows[idx];
              const complete = isRowComplete(row);
              return (
                <tr
                  className="table-row-fullwidth"
                  key={candidate.candidateNumber}
                >
                  <td className="col-status">
                    <span className="candidate-status">
                      <span
                        className="icon"
                        title={
                          complete
                            ? "Marks complete for this candidate"
                            : "Enter a valid mark and assignment for each candidate."
                        }
                      >
                        {complete ? (
                          // GREEN TICK SVG
                          <svg
                            style={{ fill: "#2B8000" }}
                            focusable="false"
                            aria-hidden="true"
                            viewBox="0 0 24 24"
                          >
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm3.88-11.71L10 14.17l-1.88-1.88a.9959.9959 0 0 0-1.41 0c-.39.39-.39 1.02 0 1.41l2.59 2.59c.39.39 1.02.39 1.41 0L17.3 9.7c.39-.39.39-1.02 0-1.41-.39-.39-1.03-.39-1.42 0z"></path>
                          </svg>
                        ) : (
                          // RED EXCLAMATION SVG
                          <svg
                            style={{ fill: "#D9221C" }}
                            aria-hidden="true"
                            viewBox="0 0 24 24"
                          >
                            <path d="M12 7c.55 0 1 .45 1 1v4c0 .55-.45 1-1 1s-1-.45-1-1V8c0-.55.45-1 1-1zm-.01-5C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm1-3h-2v-2h2v2z" />
                          </svg>
                        )}
                      </span>
                    </span>
                  </td>
                  {currentCentreType === "Lead" && isConsortiaUnit && (
                    <td className="col-number">{candidate.centreNumber}</td>
                  )}
                  <td className="col-number">{candidate.candidateNumber}</td>
                  <td className="col-name">
                    {candidate.candidateName.replace(":", ":")}
                  </td>
                  <td>
                    <label
                      className="checkbox label"
                      style={{ fontWeight: 400 }}
                    >
                      <input
                        type="checkbox"
                        checked={row.markAsAbsent}
                        onChange={(e) =>
                          handleInputChange(idx, "absent", e.target.checked)
                        }
                      />
                      Absent
                    </label>
                  </td>
                  <td>
                    <div
                      className={`mark-input-wrapper${
                        row.markAsAbsent ? " disabled" : ""
                      }`}
                    >
                      <input
                        className="input input-total-mark"
                        type="text"
                        value={row.totalCriteriaAchieved}
                        onChange={(e) =>
                          handleInputChange(
                            idx,
                            "marks",
                            e.target.value.replace(/[^0-9]/g, ""),
                          )
                        }
                        maxLength={2}
                        placeholder=""
                        inputMode="numeric"
                        pattern="[0-9]*"
                        disabled={row.markAsAbsent}
                      />
                      <span className="max-mark-text">/ {row.totalMarks}</span>
                    </div>
                  </td>

                  <td>
                    <div
                      className={`select input-assignment ${
                        row.markAsAbsent ? "is-disabled" : ""
                      }`}
                    >
                      <select
                        value={row.assignmentId}
                        onChange={(e) =>
                          handleInputChange(idx, "assignmentId", e.target.value)
                        }
                        disabled={row.markAsAbsent}
                      >
                        <option value="">-- Select assignment --</option>
                        {availableAssignments.map((assignment) => (
                        <option
                            key={assignment.assignmentId}
                            value={assignment.assignmentId}
                        >
                            {assignment.assignmentLabel}
                        </option>
                        ))}
                      </select>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MarkSubmissionTable;

MarkSubmissionTable.propTypes = {
  showError: PropTypes.bool,
  rows: PropTypes.array,
  setRows: PropTypes.func,
  candidates: PropTypes.array,
  anyRowInvalid: PropTypes.bool,
  isRowComplete: PropTypes.func,
};
